/*
Copyright (c) 2012-2022 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import axios from "axios"
import { ALL_TRANSACTIONS, API_DEFAULT_LIMIT } from "common/constants/misc"
import { transactionsConvertor } from "common/duck/convertors"
import { Transaction } from "common/types/models/transaction"
import { TroyFoodPortalEndpoint } from "constants/endpoints"
import includes from "ramda/src/includes"
import { Epic, ofType } from "redux-observable"
import { concat, of } from "rxjs"
import { delay, mapTo, switchMap } from "rxjs/operators"
import { AppState, EpicDependenciesType } from "store/store.type"
import { createReducer } from "../create-reducer"
import { actions as MerchantActionTypes } from "./merchant"
import { Transactions } from "./transactions.type"

const NAMESPACE = "TRANSACTIONS"

// actions
const RESET_TRANSACTIONS = `${NAMESPACE}/RESET_TRANSACTIONS`
const FETCH_TRANSACTIONS_NEXT = `${NAMESPACE}/FETCH_TRANSACTIONS_NEXT`
const FETCH_TRANSACTIONS_NEXT_SUCCESS = `${NAMESPACE}/FETCH_TRANSACTIONS_NEXT_SUCCESS`
const FETCH_TRANSACTIONS_FAILED = `${NAMESPACE}/FETCH_TRANSACTIONS_FAILED`
const CANCEL_PENDING_REQUESTS = `${NAMESPACE}/CANCEL_PENDING_REQUESTS`

export const actions = {
  RESET_TRANSACTIONS,
  FETCH_TRANSACTIONS_NEXT,
  FETCH_TRANSACTIONS_NEXT_SUCCESS,
  FETCH_TRANSACTIONS_FAILED,
  CANCEL_PENDING_REQUESTS,
}

type ActionTypes =
  | typeof RESET_TRANSACTIONS
  | typeof FETCH_TRANSACTIONS_NEXT
  | typeof FETCH_TRANSACTIONS_NEXT_SUCCESS
  | typeof FETCH_TRANSACTIONS_FAILED
  | typeof CANCEL_PENDING_REQUESTS

// action creators
const resetTransactions = () => ({
  type: RESET_TRANSACTIONS,
})
const fetchTransactionsNext = ({
  queryID,
  transactionID,
  startDate,
  endDate,
}: {
  queryID: StoreID
  transactionID?: TransactionID
  startDate: string
  endDate: string
}) => {
  const payload = {
    queryID,
    transactionID,
    startDate,
    endDate,
  }
  if (transactionID === ALL_TRANSACTIONS) {
    payload.transactionID = undefined
  }
  return {
    type: FETCH_TRANSACTIONS_NEXT,
    payload,
  }
}
const fetchTransactionsNextSuccess = (
  transactions: Array<Transaction>,
  dataReady: boolean,
  nextID?: SinceID,
) => ({
  type: FETCH_TRANSACTIONS_NEXT_SUCCESS,
  payload: {
    transactions,
    dataReady,
    nextID,
  },
})
const fetchTransactionsFailed = (error: string) => ({
  type: FETCH_TRANSACTIONS_FAILED,
  payload: {
    error,
  },
})
const cancelPendingRequests = () => ({
  type: CANCEL_PENDING_REQUESTS,
})
export const actionCreator = {
  resetTransactions,
  fetchTransactionsNext,
  fetchTransactionsNextSuccess,
  fetchTransactionsFailed,
  cancelPendingRequests,
}

type ActionCreatorTypes = {
  [K in keyof typeof actionCreator]: ReturnType<typeof actionCreator[K]>
}
export type ActionCreatorTypeList = ActionCreatorTypes[keyof ActionCreatorTypes]

// state
export const initialState = (): Transactions => ({
  error: null,
  fetching: false,
  transactions: [],
  transactionTypes: [],
  transactionStatus: [],
  payMethods: [],
  dataReady: false,
  cancelTokenSource: axios.CancelToken.source(),
  prevID: null,
  middleID: 0,
  nextID: 0,
})

// reducer
export const reducer = createReducer<
  Transactions,
  ActionTypes,
  ActionCreatorTypeList
>(initialState(), {
  [RESET_TRANSACTIONS]: () => {
    return initialState()
  },
  [FETCH_TRANSACTIONS_NEXT]: (state: Transactions) => ({
    ...state,
    fetching: true,
  }),
  [FETCH_TRANSACTIONS_NEXT_SUCCESS]: (
    state: Transactions,
    action: ActionCreatorTypes["fetchTransactionsNextSuccess"],
  ) => {
    const {
      transactions: payloadTransactions,
      dataReady,
      nextID = null,
    } = action.payload
    const {
      transactions,
      transactionTypes,
      transactionStatus,
      payMethods,
    } = state
    payloadTransactions.forEach(transaction => {
      const { transactionType, status, payMethod } = transaction
      transactions.push(transaction)
      if (!includes(transactionType, transactionTypes)) {
        transactionTypes.push(transactionType)
        transactionTypes.sort()
      }
      if (!includes(status, transactionStatus)) {
        transactionStatus.push(status)
        transactionStatus.sort()
      }
      if (!includes(payMethod, payMethods)) {
        payMethods.push(payMethod)
        payMethods.sort()
      }
    })
    return {
      ...state,
      fetching: !dataReady,
      dataReady,
      prevID: state.middleID,
      middleID: state.nextID,
      nextID,
    }
  },
  [FETCH_TRANSACTIONS_FAILED]: (
    state: Transactions,
    action: ActionCreatorTypes["fetchTransactionsFailed"],
  ) => {
    return {
      ...state,
      error: action.payload.error,
      dataReady: false,
      fetching: false,
    }
  },
})

// epic
const propagateToResetTransactionsEpic: Epic<
  ActionCreatorTypeList,
  ActionCreatorTypeList
> = action$ =>
  action$.pipe(
    ofType(MerchantActionTypes.RESET_DETAIL),
    mapTo(resetTransactions()),
  )
const cancelPendingRequestsEpic: Epic<
  ActionCreatorTypeList,
  ActionCreatorTypeList,
  AppState
> = (action$, store$) =>
  action$.pipe(
    ofType(CANCEL_PENDING_REQUESTS),
    switchMap(() => {
      store$.value.transactions.cancelTokenSource.cancel()
      return of(resetTransactions()).pipe(delay(100))
    }),
  )
const fetchTransactionsEpic: Epic<
  ActionCreatorTypeList,
  ActionCreatorTypeList,
  AppState,
  EpicDependenciesType
> = (action$, store$, { httpRequest }) =>
  action$.pipe(
    ofType(FETCH_TRANSACTIONS_NEXT),
    switchMap((action: ActionCreatorTypes["fetchTransactionsNext"]) => {
      const { queryID, transactionID, startDate, endDate } = action.payload
      const cancelTokenSource = store$.value.transactions.cancelTokenSource
      const nextID = store$.value.transactions.nextID
      const params = {
        merchantID: queryID,
        startTime: startDate,
        endTime: endDate,
        limit: API_DEFAULT_LIMIT,
        transactionID,
        sinceID: nextID!,
      }
      return httpRequest
        .GET<APIGetTransactionsResp>({
          endpoint: TroyFoodPortalEndpoint.getTransactionsV1,
          params,
          cancelToken: cancelTokenSource.token,
        })
        .pipe(
          switchMap(res => {
            if (res.data === null) {
              return of(fetchTransactionsFailed("error"))
            }
            const transactions = transactionsConvertor(res.data.transactions)
            const sinceID = res.data.sinceID
            const dataReady = sinceID === undefined || sinceID === 0
            if (dataReady) {
              return of(fetchTransactionsNextSuccess(transactions, dataReady))
            }
            return concat(
              of(
                fetchTransactionsNextSuccess(transactions, dataReady, sinceID),
              ),
              of(
                fetchTransactionsNext({
                  queryID,
                  transactionID,
                  startDate,
                  endDate,
                }),
              ),
            )
          }),
        )
    }),
  )

export const epics = {
  propagateToResetTransactionsEpic,
  fetchTransactionsEpic,
  cancelPendingRequestsEpic,
}
